import React from "react";
import { 
  ShieldCheck, CheckCircle2, AlertCircle, Sparkles, FileText, Lock, Building2
} from "lucide-react";
import { getMethodologyArticleBySlug, MethodologyArticle } from "../../utils/methodologyRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface MethodologyPageProps {
  slug: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function MethodologyPage({ slug, onNavigate, onLogin = () => {}, currentUser }: MethodologyPageProps) {
  const article: MethodologyArticle | null = getMethodologyArticleBySlug(slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Methodology Page Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The requested technical methodology page does not exist.</p>
        <button
          onClick={() => onNavigate("/how-it-works")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View How It Works Guide
        </button>
      </div>
    );
  }

  const currentRoute = `/methodology/${article.slug}`;

  useSeoMetadata({
    title: article.title,
    description: article.metaDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": article.h1,
      "description": article.metaDescription,
      "url": `https://getbiznearyou.com${currentRoute}`
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs
          items={[
            { label: "Methodology", path: "/how-it-works" },
            { label: article.h1, path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Header */}
        <header className="mb-8">
          <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 inline-block mb-3">
            {article.category}
          </span>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
            {article.h1}
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed bg-white p-4 rounded-xl border border-slate-200">
            {article.summary}
          </p>
        </header>

        {/* Sections */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 mb-8 space-y-8 shadow-sm">
          {article.sections.map((sec, i) => (
            <section key={i} className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">{sec.heading}</h2>
              {sec.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  {p}
                </p>
              ))}
              {sec.bulletPoints && (
                <ul className="space-y-2 pl-2">
                  {sec.bulletPoints.map((bp, bpIdx) => (
                    <li key={bpIdx} className="text-xs sm:text-sm text-slate-700 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{bp}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
